import {quizActions, QuizProvider, useQuiz} from "../providers/QuizProvider.tsx";
import YesNoQuestion from "../components/quiz/YesNoQuestion.tsx";
import PracticeSelector from "../components/quiz/PracticeSelector.tsx";
import QuizNavigation from "../components/quiz/QuizNavigation.tsx";
import {type ReactNode, useEffect} from "react";
import TextQuestion from "../components/quiz/TextQuestion.tsx";
import {trackQuizSubmission} from "../lib/algolia-insights.ts";
import {getContactPractice} from "../lib/utils.ts";

interface QuizStep {
    title: string;
    description?: string;
    content: ReactNode;
}

const MensQuizContent = () => {
    const {state, dispatch} = useQuiz();

    // Check if practice is already known (from window global or sessionStorage)
    const practice = getContactPractice();

    useEffect(() => {
        if (practice) {
            dispatch(quizActions.updateFormData({selectedPractice: practice}));
        }
    }, []);

    // Calculate symptom score
    const calculateScore = () => {
        const yesAnswers = Object.values(state.formData).filter(value => value === true);
        return yesAnswers.length;
    };

    const getTotalQuestions = () => {
        return 12; // Total yes/no questions
    };

    const getScoreAnalysis = (score: number) => {
        const percentage = (score / getTotalQuestions()) * 100;

        if (percentage >= 70) {
            return {
                level: "High",
                color: "red",
                message: "You're experiencing a significant number of symptoms that may indicate low testosterone or hormonal imbalance. We strongly recommend connecting with a healthcare provider specializing in men's hormone therapy."
            };
        } else if (percentage >= 40) {
            return {
                level: "Moderate",
                color: "orange",
                message: "You're experiencing several symptoms that could be related to hormonal changes. A consultation with a hormone specialist could help address these concerns."
            };
        } else if (percentage > 0) {
            return {
                level: "Mild",
                color: "yellow",
                message: "You're experiencing some symptoms that may benefit from evaluation. Consider speaking with a healthcare provider about your concerns."
            };
        } else {
            return {
                level: "Low",
                color: "green",
                message: "You're experiencing few symptoms at this time. However, if you have concerns, we encourage you to consult with a healthcare provider."
            };
        }
    };

    // Define your quiz steps here
    const steps: QuizStep[] = [
        {
            title: "Energy, Mood & Mental Clarity",
            description: "Low testosterone can significantly affect your energy levels and mental state",
            content: (
                <>
                    {
                        [
                            'Do you feel constantly tired or fatigued despite good sleep?',
                            'Have you been experiencing brain fog, forgetfulness, or lack of motivation?',
                            'Do you feel more anxious or depressed than usual?'
                        ]
                            .map((q, idx) => <YesNoQuestion name={q} label={q} key={'energy-' + idx}/>)
                    }
                </>
            )
        },
        {
            title: "Physical Changes & Muscle Health",
            description: "Testosterone plays a key role in maintaining muscle mass and healthy body composition",
            content: (
                <>
                    {
                        [
                            'Are you losing muscle mass or strength despite working out?',
                            'Have you gained belly fat or are struggling to lose weight?',
                            'Do you have reduced stamina or endurance during workouts?'
                        ]
                            .map((q, idx) => <YesNoQuestion name={q} label={q} key={'physical-' + idx}/>)
                    }
                </>
            )
        },
        {
            title: "Sexual Health & Hormone Function",
            description: "These symptoms are often the most noticeable signs of low testosterone",
            content: (
                <>
                    {
                        [
                            'Do you have a lower sex drive than you used to?',
                            'Are you experiencing erectile dysfunction or reduced performance?',
                            'Do you have fewer spontaneous morning erections?'
                        ]
                            .map((q, idx) => <YesNoQuestion name={q} label={q} key={'sexual-' + idx}/>)
                    }
                </>
            )
        },
        {
            title: "Other Key Symptoms",
            description: "Additional signs that may indicate hormonal imbalance",
            content: (
                <>
                    {
                        [
                            'Do you struggle to focus or stay mentally sharp?',
                            'Have you noticed thinning hair or more body fat?',
                            'Do you feel more irritable, withdrawn, or less confident?'
                        ]
                            .map((q, idx) => <YesNoQuestion name={q} label={q} key={'other-' + idx}/>)
                    }
                </>
            )
        },
        {
            title: "Your Health Assessment Results",
            description: "Based on your responses",
            content: (() => {
                const score = calculateScore();
                const analysis = getScoreAnalysis(score);

                return (
                    <div className="space-y-6">
                        <div className="text-center py-8 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
                            <div className="mb-4">
                                <div
                                    className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-white shadow-lg">
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-blue-600">{score}</div>
                                        <div className="text-sm text-gray-600">out of {getTotalQuestions()}</div>
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-2xl font-bold mb-2"
                                style={{color: analysis.color === 'red' ? '#DC2626' : analysis.color === 'orange' ? '#EA580C' : analysis.color === 'yellow' ? '#CA8A04' : '#16A34A'}}>
                                {analysis.level} Symptom Level
                            </h3>
                            <p className="text-gray-700 max-w-2xl mx-auto px-4">
                                {analysis.message}
                            </p>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-blue-700">
                                        <strong>Next Step:</strong> {practice
                                        ? 'Provide your contact information so your provider can reach out to you.'
                                        : 'Select a healthcare provider near you to discuss your symptoms and explore treatment options.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()
        },
        ...(!practice ? [{
            title: "Find a Provider Near You",
            description: "Please select a healthcare provider to continue. They will review your assessment and help create a personalized treatment plan.",
            content: (
                <>
                    <PracticeSelector
                        name="selectedPractice"
                        label="Select a Provider to Continue"
                        required
                    />
                </>
            )
        }] : []),
        {
            title: "Your Contact Information",
            description: "So your selected provider can reach you",
            content: (
                <>
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                            Your selected provider will use this information to schedule a consultation and discuss your
                            assessment results.
                        </p>
                    </div>
                    <TextQuestion name={'firstName'} label={'First Name'} type={"text"} required/>
                    <TextQuestion name={'lastName'} label={'Last Name'} type={"text"} required/>
                    <TextQuestion name={'email'} label={'Email Address'} type={"email"} required/>
                    <TextQuestion name={'phone'} label={'Phone Number'} type={"tel"} required/>
                </>
            )
        },
        {
            title: "Review & Submit Your Assessment",
            description: "Confirm your information before submitting",
            content: (
                <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="font-semibold text-green-800 mb-2">✓ Assessment Complete</h3>
                        <p className="text-sm text-green-700">
                            You've completed the health assessment with {calculateScore()} symptoms identified. Your
                            results will be shared with your selected provider.
                        </p>
                    </div>

                    <div className="bg-white border rounded-lg p-6">
                        <h3 className="font-semibold text-lg mb-4">Selected Provider:</h3>
                        {state.formData.selectedPractice ? (
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="font-medium text-blue-900">{state.formData.selectedPractice.name}</p>
                                <p className="text-sm text-blue-700 mt-1">{state.formData.selectedPractice.address}</p>
                                {state.formData.selectedPractice.phone_formatted && (
                                    <p className="text-sm text-blue-700 mt-1">Phone: {state.formData.selectedPractice.phone_formatted}</p>
                                )}
                            </div>
                        ) : (
                            <p className="text-red-600">No provider selected</p>
                        )}
                    </div>

                    <div className="bg-white border rounded-lg p-6">
                        <h3 className="font-semibold text-lg mb-4">Your Contact Information:</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex">
                                <span className="font-medium w-32">Name:</span>
                                <span
                                    className="text-gray-700">{state.formData.firstName} {state.formData.lastName}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-32">Email:</span>
                                <span className="text-gray-700">{state.formData.email}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-32">Phone:</span>
                                <span className="text-gray-700">{state.formData.phone}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    const currentStepData = steps[state.currentStep];

    // Compute step indices dynamically based on whether practice is pre-selected
    const resultsStepIndex = 4;
    const practiceSelectorStepIndex = practice ? -1 : 5;
    const contactStepIndex = practice ? 5 : 6;

    // Check if current step can proceed
    const getStepValidation = () => {
        const currentStep = state.currentStep;

        // Results step - custom button text
        if (currentStep === resultsStepIndex) {
            return {
                canProceed: true,
                nextButtonText: practice ? 'Continue →' : 'Select a Provider →',
                validationMessage: undefined
            };
        }

        // Practice selector step (only when practice is not pre-selected)
        if (currentStep === practiceSelectorStepIndex) {
            const hasSelected = !!state.formData.selectedPractice;
            return {
                canProceed: hasSelected,
                nextButtonText: hasSelected ? 'Continue to Contact Info →' : 'Select a Provider',
                validationMessage: hasSelected ? undefined : 'Please select a healthcare provider to continue'
            };
        }

        // Contact information step - all fields required
        if (currentStep === contactStepIndex) {
            const {firstName, lastName, email, phone} = state.formData;
            const allFieldsFilled = firstName && lastName && email && phone;

            if (!allFieldsFilled) {
                return {
                    canProceed: false,
                    validationMessage: 'Please fill in all contact information fields to continue'
                };
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (email && !emailRegex.test(email)) {
                return {
                    canProceed: false,
                    validationMessage: 'Please enter a valid email address'
                };
            }

            return {
                canProceed: true,
                nextButtonText: 'Review & Submit →'
            };
        }

        return {
            canProceed: true
        };
    };

    const validateCurrentStep = (): boolean => {
        // Additional validation if needed
        return true;
    };

    const handleSubmit = async () => {
        // Prevent double submission
        if (state.isSubmitting) {
            return;
        }

        // Set submitting state to true
        dispatch(quizActions.setSubmitting(true));

        const score = calculateScore();
        const analysis = getScoreAnalysis(score);

        // Separate symptom questions from other form data
        const {firstName, lastName, email, phone, selectedPractice, ...symptoms} = state.formData;

        // Convert boolean values to "Yes"/"No"
        const symptomsAsYesNo = Object.fromEntries(
            Object.entries(symptoms).map(([key, value]) => [
                key,
                typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value
            ])
        );

        const leadData = {
            practice_id: selectedPractice?.id,
            source: "website",
            lead_type: "Men's Hormone Quiz",
            data: {
                first_name: firstName,
                last_name: lastName,
                email: email,
                phone: phone,
                message: `Men's health assessment completed. Symptom level: ${analysis.level} (${score}/${getTotalQuestions()} symptoms identified)`,
                assessment_score: score,
                assessment_level: analysis.level,
                ...symptomsAsYesNo
            }
        };

        trackQuizSubmission({
            gender: "Men",
            practice_myevexias_id: selectedPractice.external_id,
            assessment_level: analysis.level,
            assessment_score: score,
            practice_name: selectedPractice.name,
            practice_nr_id: selectedPractice.id
        })

        try {
            const response = await fetch('https://evexias.nativerank.dev/api/leads', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(leadData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Lead submitted successfully:', result);

            // Redirect to provider page with results flag
            window.location.href = `/hormone-replacement-therapy-provider/${selectedPractice?.slug}?results=1`;
        } catch (error) {
            console.error('Error submitting lead:', error);
            dispatch(quizActions.setSubmitting(false));
            alert('There was an error submitting your assessment. Please try again or contact the provider directly.');
        }
    };

    if (state.error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                Error: {state.error}
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto relative">
            {/* Sticky Progress indicator */}
            <div className="sticky top-0 left-0 right-0 z-50 bg-gray-100  -mx-4 px-4 sm:-mx-0 sm:px-0">
                <div className="bg-white  shadow-md p-4 max-w-2xl mx-auto">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600 font-medium">
                            Step {state.currentStep + 1} of {steps.length}
                        </span>
                        <span className="text-sm font-bold text-blue-600">
                            {Math.round(((state.currentStep + 1) / steps.length) * 100)}% Complete
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{width: `${((state.currentStep + 1) / steps.length) * 100}%`}}
                        />
                    </div>
                </div>
            </div>

            {/* Step content */}
            <div className="bg-white rounded-lg shadow-lg p-8 pt-12 pb-0 md:pb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {currentStepData.title}
                </h2>
                {currentStepData.description && (
                    <p className="text-gray-600 mb-6">{currentStepData.description}</p>
                )}

                <div className="mt-6 min-h-[calc(100dvh-300px)] md:min-h-0">
                    {currentStepData.content}
                </div>

                <QuizNavigation
                    totalSteps={steps.length}
                    onSubmit={handleSubmit}
                    validateStep={validateCurrentStep}
                    {...getStepValidation()}
                />
            </div>
        </div>
    );
};

const MensQuiz = () => {
    return (
        <QuizProvider>
            <div className="min-h-screen  py-12 ">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">Men's Hormone Health Assessment</h1>
                        <p className="text-gray-600">Discover your symptom profile and connect with a specialist</p>
                    </div>
                    <MensQuizContent/>
                </div>
            </div>
        </QuizProvider>
    );
};

export default MensQuiz;
